import { toast } from 'sonner';
import { Share2 } from '../../icons';
import { cn } from '../../lib/utils';

export interface ShareButtonProps {
  /** Lazily computes the URL to copy at click time (deep-link is read fresh).
   *  `null` means no URL could be built (e.g. sharing disabled). */
  getUrl: () => string | null;
  /** Accessible label + tooltip text. */
  label?: string;
  /** Optional override/extension of the default icon-button styling. */
  className?: string;
}

/**
 * Small header icon-button that copies a built share URL to the clipboard and
 * confirms with a toast. Renders one consistent look everywhere — a bordered
 * round icon button matching the other drawer/header icon buttons. Callers
 * should NOT override the className so the share icon stays identical across
 * every screen and drawer.
 */
export function ShareButton({ getUrl, label = 'Share link', className }: ShareButtonProps) {
  const onClick = async () => {
    try {
      const url = getUrl();
      if (!url) {
        toast.error('Could not create link');
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed — check clipboard permissions');
    }
  };

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        'inline-flex size-9 items-center justify-center rounded-full border border-[#E5E5E5] text-[#737373] transition-colors hover:bg-[#F4F4F2] hover:text-[#171717]',
        className,
      )}
    >
      <Share2 size={16} />
    </button>
  );
}
